import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class StorageTimeoutService implements OnDestroy {
    private readonly ONE_HOUR_MS = 1000 * 60 * 60; // 1 hour in milliseconds (3,600,000 ms)
    private checkInterval: any = null;

    constructor(@Inject(PLATFORM_ID) private platformId: Object) {
        if (isPlatformBrowser(this.platformId)) {
            // Check immediately upon initialization
            this.checkInactivity();
            this.initMonitoring();
        }
    }

    /**
     * Inspects all items in localStorage to find the latest lastActive timestamp.
     * If the user was inactive for 1 hour or more, clears localStorage and triggers a hard reload.
     * Returns true if inactive and reload triggered, false otherwise.
     */
    public checkInactivity(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }

        try {
            if (localStorage.length === 0) {
                return false;
            }

            let latestActiveTimestamp: number | null = null;

            // Iterate over all items in localStorage to inspect lastActive
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key) continue;

                const itemStr = localStorage.getItem(key);
                if (!itemStr) continue;

                // Direct check for key === 'lastActive'
                if (key === 'lastActive') {
                    const directTs = Number(itemStr);
                    if (!isNaN(directTs) && directTs > 0) {
                        latestActiveTimestamp = Math.max(latestActiveTimestamp ?? 0, directTs);
                    }
                    continue;
                }

                try {
                    const parsed = JSON.parse(itemStr);
                    if (parsed && typeof parsed === 'object') {
                        if (parsed.lastActive) {
                            const ts = Number(parsed.lastActive);
                            if (!isNaN(ts) && ts > 0) {
                                latestActiveTimestamp = Math.max(latestActiveTimestamp ?? 0, ts);
                            }
                        } else if (parsed.data && parsed.data.lastActive) {
                            const ts = Number(parsed.data.lastActive);
                            if (!isNaN(ts) && ts > 0) {
                                latestActiveTimestamp = Math.max(latestActiveTimestamp ?? 0, ts);
                            }
                        } else if (Array.isArray(parsed)) {
                            for (const item of parsed) {
                                if (item && item.lastActive) {
                                    const ts = Number(item.lastActive);
                                    if (!isNaN(ts) && ts > 0) {
                                        latestActiveTimestamp = Math.max(latestActiveTimestamp ?? 0, ts);
                                    }
                                }
                            }
                        }
                    } else if (typeof parsed === 'number' && key === 'lastActive') {
                        latestActiveTimestamp = Math.max(latestActiveTimestamp ?? 0, parsed);
                    }
                } catch {
                    // Ignore malformed items
                }
            }
            if (latestActiveTimestamp !== null) {
                const elapsed = Date.now() - latestActiveTimestamp;
                if (elapsed >= this.ONE_HOUR_MS) {
                    this.clearStorageAndReload();
                    return true;
                }
            }
        } catch (e) {
            console.error('Error checking inactivity in StorageTimeoutService:', e);
        }

        return false;
    }

    /**
     * Updates activity timestamp in localStorage and checks session
     */
    public touchActivity(): void {
        if (isPlatformBrowser(this.platformId)) {
            try {
                localStorage.setItem('lastActive', Date.now().toString());
            } catch (e) {
                console.error('Failed to update lastActive in localStorage:', e);
            }
        }
    }

    /**
     * Clears all localStorage items and forces a hard reload of the page
     */
    public clearStorageAndReload(): void {
        if (isPlatformBrowser(this.platformId)) {
            try {
                localStorage.clear();
            } catch (e) {
                console.error('Failed to clear localStorage:', e);
            }
            window.location.reload();
        }
    }

    /**
     * Sets up periodic checking and visibility/focus listeners
     */
    private initMonitoring(): void {
        // Periodic check every 1 minute
        this.checkInterval = setInterval(() => {
            this.checkInactivity();
        }, 60 * 1000);

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.checkInactivity();
                }
            });
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('focus', () => {
                this.checkInactivity();
            });
        }
    }

    ngOnDestroy(): void {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }
}
